import { signal } from '@angular/core';
import { SortMeta } from 'primeng/api';
import { BehaviorSubject, skip } from 'rxjs';
import { ColumnSort } from '../../osp-ui/components/data/table/contract/column-sort.contract';
import { PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE } from '../constants/pageable-datasource.const';
import { convertSortMetaToColumnSort } from '../utils/convert-sort-meta-to-column-sort';
import { isSortMeta } from '../utils/is-sort-meta';
import { PageableRequestParams } from './pageable-request-params.contract';
import { PagedResponse } from './paged-response.contract';

export abstract class PageableDatasource<T> {
  private _pageableData$ = new BehaviorSubject<PagedResponse<T> | null>(null);
  protected _data$ = new BehaviorSubject<T[] | null>(null);
  private _pageSize$ = new BehaviorSubject<number>(PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE);
  private _pageIndex$ = new BehaviorSubject<number>(0);
  private _totalElement$ = new BehaviorSubject<number>(0);
  private _sort$ = new BehaviorSubject<ColumnSort | SortMeta | null>(null);
  protected _filteredData$ = new BehaviorSubject<T[] | null>(null);

  public $isLoading = signal<boolean | undefined>(undefined);
  public $isNoData = signal<boolean | undefined>(undefined);

  readonly data$ = this._data$.asObservable();
  readonly pageSize$ = this._pageSize$.asObservable();
  readonly pageIndex$ = this._pageIndex$.asObservable();
  readonly totalElement$ = this._totalElement$.asObservable();
  readonly sort$ = this._sort$.asObservable();

  get data(): T[] | null {
    return this._data$.getValue();
  }
  get pageSize(): number {
    return this._pageSize$.getValue();
  }
  get pageIndex(): number {
    return this._pageIndex$.getValue();
  }
  get totalElement(): number {
    return this._totalElement$.getValue();
  }
  get sort(): ColumnSort | SortMeta | null {
    return this._sort$.getValue();
  }
  get currentPageableRequestParams(): PageableRequestParams {
    return {
      page: this.pageIndex,
      size: this.pageSize,
      sort: this.sort,
    };
  }

  constructor() {
    this.initListeners();
  }

  // Abstract method
  // Child class must implement the approach to get data (usually from an API)
  // Result of the request must be stored with "setPageableData" method
  protected abstract requestData(params: PageableRequestParams): void;

  protected setPageableData(data: PagedResponse<T>): void {
    this._pageableData$.next(data);
  }

  private initListeners(): void {
    this._pageableData$.pipe(skip(1)).subscribe((data) => {
      const content = data?.content ?? [];
      this._data$.next(content);
      this._filteredData$.next(content);
      this._pageSize$.next(data?.size ?? PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE);
      this._pageIndex$.next(data?.number ?? 0);
      this._totalElement$.next(data?.totalElements ?? 0);

      this.$isLoading.set(false);
      this.$isNoData.set(content.length === 0);
    });
  }

  updateData(params?: Partial<PageableRequestParams>): void {
    const { page = this.pageIndex, size = this.pageSize, sort = this.sort } = params ?? {};

    this.$isLoading.set(true);
    this.$isNoData.set(undefined);
    this.requestData({ page, size, sort });
  }

  changePageIndex(page: number, update = true): void {
    this._pageIndex$.next(page);
    if (update) {
      this.updateData({ page });
    }
  }

  changePageSize(size: number, update = true): void {
    if (size !== this._pageSize$.getValue()) {
      this.changePageIndex(0, false);
    }
    this._pageSize$.next(size);
    if (update) {
      this.updateData({ size });
    }
  }

  changeSorting(incomingSort: ColumnSort | SortMeta, update = true): void {
    let sort = incomingSort;

    if (isSortMeta(sort)) {
      sort = convertSortMetaToColumnSort(sort);
    }
    this._sort$.next(sort);

    if (update) {
      this.updateData({ sort });
    }
  }
}
